import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import type { SafetySmsSettings } from '../../constants/safetySmsSettings';
import type { SxProps, Theme } from '@mui/material';

type Props = {
  value: SafetySmsSettings;
  onChange: (next: SafetySmsSettings) => void;
  onSave: () => void;
  onResetDefaults: () => void;
  saving?: boolean;
  cardSx?: SxProps<Theme>;
};

const SafetySmsSettingsCard: React.FC<Props> = ({
  value,
  onChange,
  onSave,
  onResetDefaults,
  saving = false,
  cardSx,
}) => (
  <Card sx={cardSx}>
    <CardHeader
      title="현장 안전 SMS (알리고)"
      subheader="통합 관제 경고/위험 알림 문자. 시설별 수신 번호는 시설 정보의 현장 안전관리자 연락처(safety_phone)."
      action={
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={onResetDefaults}
            disabled={saving}
          >
            기본값
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSave}
            disabled={saving}
          >
            {saving ? '저장 중…' : '저장'}
          </Button>
        </Box>
      }
    />
    <CardContent>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth margin="dense">
            <InputLabel>발송 방식</InputLabel>
            <Select
              label="발송 방식"
              value={value.provider}
              onChange={(e) =>
                onChange({
                  ...value,
                  provider: e.target.value as SafetySmsSettings['provider'],
                })
              }
            >
              <MenuItem value="auto">자동 (알리고 → 웹훅 → 로그)</MenuItem>
              <MenuItem value="aligo">알리고 직접</MenuItem>
              <MenuItem value="webhook">웹훅 중계</MenuItem>
              <MenuItem value="log">로그만 (전송 안 함)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            margin="dense"
            type="number"
            label="HTTP 타임아웃(초)"
            value={value.timeout_sec}
            inputProps={{ min: 3, max: 60 }}
            onChange={(e) =>
              onChange({ ...value, timeout_sec: Number(e.target.value) })
            }
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        알리고
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            margin="dense"
            label="API URL"
            value={value.aligo_api_url}
            onChange={(e) => onChange({ ...value, aligo_api_url: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            margin="dense"
            label="API KEY"
            value={value.aligo_api_key}
            onChange={(e) => onChange({ ...value, aligo_api_key: e.target.value })}
            type="password"
            autoComplete="off"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            margin="dense"
            label="USER ID"
            value={value.aligo_user_id}
            onChange={(e) => onChange({ ...value, aligo_user_id: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            margin="dense"
            label="발신번호 (등록번호)"
            value={value.aligo_sender}
            onChange={(e) => onChange({ ...value, aligo_sender: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            margin="dense"
            label="LMS 제목"
            value={value.aligo_lms_title}
            onChange={(e) => onChange({ ...value, aligo_lms_title: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={value.aligo_test_mode}
                onChange={(e) =>
                  onChange({ ...value, aligo_test_mode: e.target.checked })
                }
              />
            }
            label="알리고 테스트 모드 (testmode_yn=Y, 과금 없음)"
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        웹훅 (선택)
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            margin="dense"
            label="웹훅 URL"
            value={value.webhook_url}
            onChange={(e) => onChange({ ...value, webhook_url: e.target.value })}
            placeholder="https://..."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth margin="dense">
            <InputLabel>웹훅 페이로드</InputLabel>
            <Select
              label="웹훅 페이로드"
              value={value.webhook_format}
              onChange={(e) =>
                onChange({
                  ...value,
                  webhook_format: e.target.value as SafetySmsSettings['webhook_format'],
                })
              }
            >
              <MenuItem value="generic">JSON (phone / message)</MenuItem>
              <MenuItem value="aligo_form">알리고 form 필드</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={value.webhook_batch}
                onChange={(e) =>
                  onChange({ ...value, webhook_batch: e.target.checked })
                }
              />
            }
            label="수신자 일괄 1회 POST"
          />
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

export default SafetySmsSettingsCard;
